import { useState } from "react";
import { MicButton } from "@/components/MicButton";

// One-question-at-a-time flow matching the assessment format.
// steps: [{ id, kicker, question, kind: "choice"|"text", options?, placeholder? }]
export default function CheckinFlow({ steps, onComplete, submitting, ctaLabel = "Save", flowTestId = "checkin-flow" }) {
  const [i, setI] = useState(0);
  const [answers, setAnswers] = useState({});
  const [advancing, setAdvancing] = useState(false);

  const s = steps[i];
  const last = i === steps.length - 1;
  const answeredChoice = answers[s.id] !== undefined;
  const progress = ((i + (s.kind === "text" ? 1 : (answeredChoice ? 1 : 0))) / steps.length) * 100;

  const advance = (next) => {
    if (last) onComplete(next);
    else { setI(i + 1); setAdvancing(false); }
  };
  const choose = (v) => {
    if (advancing || submitting) return;
    const next = { ...answers, [s.id]: v };
    setAnswers(next);
    setAdvancing(true);
    setTimeout(() => advance(next), 240);
  };

  return (
    <div className="w-full max-w-2xl" data-testid={flowTestId}>
      <div className="mb-3 flex items-center justify-between text-xs lg:text-sm text-[#291B2D]/50">
        <span>{s.kicker}</span>
        <span>{i + 1} of {steps.length}</span>
      </div>
      <div className="h-1 bg-[#291B2D]/10 rounded-full overflow-hidden mb-6">
        <div className="h-full bg-[#C89B55] transition-all" style={{ width: `${progress}%` }} />
      </div>
      <div className="bg-white rounded-3xl p-8 lg:p-10 card-soft animate-fade-up" key={i}>
        <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold mb-3">{s.kicker}</p>
        <h2 className="font-display text-2xl lg:text-3xl leading-snug mb-6">{s.question}</h2>

        {s.kind === "choice" ? (
          <div className="space-y-2 lg:space-y-3">
            {s.options.map((o) => (
              <button key={o.v} data-testid={`flow-answer-${s.id}-${o.v}`} onClick={() => choose(o.v)} disabled={submitting || advancing}
                className={`w-full text-left p-4 lg:p-5 rounded-2xl border transition-smooth text-sm lg:text-base ${answers[s.id] === o.v ? "bg-[#291B2D] text-white border-[#291B2D]" : "bg-[#F7F2EA] border-transparent hover:bg-[#EFE7DA]"}`}>
                {o.label}
              </button>
            ))}
          </div>
        ) : (
          <>
            <div className="relative">
              <textarea data-testid={`flow-text-${s.id}`} value={answers[s.id] || ""}
                onChange={(e) => setAnswers((a) => ({ ...a, [s.id]: e.target.value }))}
                placeholder={s.placeholder || "Write as much or as little as you like, or tap the mic to speak…"}
                className="w-full bg-[#FCFAF6] border border-[#E5DED4] rounded-xl p-4 pr-14 min-h-[120px] focus:outline-none focus:ring-2 focus:ring-[#C89B55]" />
              <MicButton testid={`flow-dictate-${s.id}`} className="absolute bottom-3 right-3 h-9 w-9"
                onAppend={(t) => setAnswers((a) => ({ ...a, [s.id]: (a[s.id] ? a[s.id] + " " + t : t) }))} />
            </div>
            <button data-testid={`flow-continue-${s.id}`} onClick={() => advance({ ...answers })} disabled={submitting}
              className="w-full mt-5 rounded-full bg-[#291B2D] text-[#FCFAF6] hover:bg-[#352238] px-7 py-3.5 font-medium disabled:opacity-40 transition-all">
              {submitting && last ? "Saving…" : (last ? ctaLabel : "Continue")}
            </button>
          </>
        )}

        <div className="mt-6 flex items-center justify-between">
          {i > 0 ? (
            <button data-testid="flow-back" onClick={() => { if (!advancing && !submitting) setI(i - 1); }} className="text-sm text-[#291B2D]/50 hover:text-[#291B2D] px-1 py-2">← Back</button>
          ) : <span />}
          {s.kind === "choice" && <span className="text-xs text-[#291B2D]/40">Tap an answer to continue</span>}
        </div>
      </div>
    </div>
  );
}
